import {
  FormLabel,
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  Radio,
  RadioProps,
  SxProps,
  useRadioGroup,
} from "@mui/material";
import React, { ReactNode } from "react";

import useUniqueId from "../../utils/useUniqueId";
import { FormControl } from "../FormControl/FormControl";
import { FormControlLabel } from "../FormControlLabel/FormControlLabel";

export { Radio, RadioProps, MuiRadioGroup, useRadioGroup };

export const RadioColors = [
  "default",
  "primary",
  "secondary",
  "error",
  "info",
  "success",
  "warning",
] as const;

export const RadioSizes = ["small", "medium"] as const;

export type RadioOption = {
  label: ReactNode;
  value: string;
  disabled?: boolean;
  sx?: SxProps;
};

export interface RadioGroupProps extends MuiRadioGroupProps {
  options: RadioOption[];
  disabled?: boolean;
  label?: string;
  id?: string;
  listSx?: SxProps;
  groupSx?: SxProps;
  wrapperSx?: SxProps;
  radioProps?: RadioProps;
  disableTypography?: boolean;
}

export const RadioGroup = React.forwardRef(
  (
    {
      options,
      disabled: radioGroupDisabled,
      label,
      id,
      listSx,
      groupSx,
      wrapperSx,
      disableTypography,
      radioProps = {},
      ...radioGroupProps
    }: RadioGroupProps,
    ref: React.Ref<unknown>
  ) => {
    const uniqueId = useUniqueId();
    const autoId = id || uniqueId;
    return (
      <FormControl sx={wrapperSx}>
        {label && (
          <FormLabel
            id={autoId}
            sx={{ fontWeight: 500, color: "common.black" }}
          >
            {label}
          </FormLabel>
        )}
        <MuiRadioGroup
          {...radioGroupProps}
          ref={ref}
          aria-labelledby={autoId}
          sx={groupSx}
        >
          {options?.map(({ label, value, disabled, sx }) => (
            <FormControlLabel
              key={value}
              value={value}
              disableTypography={disableTypography}
              control={
                <Radio
                  {...radioProps}
                  disabled={radioGroupDisabled || disabled}
                  sx={sx}
                />
              }
              label={label}
              sx={listSx}
            />
          ))}
        </MuiRadioGroup>
      </FormControl>
    );
  }
);
